import React, { Fragment, useState, useMemo } from "react";
import { Popover, Transition } from "@headlessui/react";
import {useI18n} from "../../i18n";

interface CustomPopoverProps<T = any> {
    icon?: string | React.ReactNode;
    items: T[];
    label?: string;
    onSelect: (item: T) => void;
    getLabel?: (item: T) => string;
    emptyMessage?: string;
    enableSearch?: boolean;
    placeholder?: string;
}

const CustomPopover = <T,>({
                               icon,
                                 label,
                               items,
                               onSelect,
                               getLabel = (item) => (item as any).section || 'Untitled',
                               emptyMessage,
                               enableSearch = false,
                               placeholder,
                           }: CustomPopoverProps<T>) => {
    const {t} = useI18n();
    const [query, setQuery] = useState("");

    const filteredItems = useMemo(() => {
        if (!enableSearch || !query.trim()) return items;
        return items.filter((item) =>
            getLabel(item).toLowerCase().includes(query.toLowerCase())
        );
    }, [items, query, getLabel, enableSearch]);

    return (
        <Popover className="flex relative">
            <Popover.Button>
                {icon ? ( typeof icon === 'string' ?
                    <img
                        src={icon}
                        alt={t('components.customPopover.menuAlt')}
                        className="w-[20px] xl:w-[40px] mr-2 cursor-pointer"
                    /> : icon
                ) : (
                    <div className="px-3 py-1 text-xs rounded bg-ORANGE_BLACK text-white hover:bg-neutral-950-700">
                        {label ?? t('components.customPopover.select')}
                    </div>
                )}
            </Popover.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
            >
                <Popover.Panel className="absolute right-0 bottom-full mb-2 z-[9999999] w-48 rounded-md shadow-lg bg-ORANGE_BLACK ring-1 ring-black border border-neutral-800 ring-opacity-5 focus:outline-none">
                    <div className="flex flex-col py-2 text-sm text-white max-h-60 overflow-y-auto custom-scrollbar">
                        {enableSearch && (
                            <div className="px-3 pb-2">
                                <input
                                    type="text"
                                    placeholder={placeholder || t('components.customPopover.searchPlaceholder')}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    className="w-full px-2 py-1 rounded bg-gray-800 border border-gray-600 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500"
                                />
                            </div>
                        )}

                        {filteredItems.length === 0 ? (
                            <div className="px-4 py-2 text-gray-400">{emptyMessage || t('components.customPopover.nothingFound')}</div>
                        ) : (
                            filteredItems.map((item, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => onSelect(item)}
                                    className="w-full text-left px-4 py-2 hover:bg-orange-700 focus:bg-orange-900"
                                >
                                    {getLabel(item)}
                                </button>
                            ))
                        )}
                    </div>
                </Popover.Panel>
            </Transition>
        </Popover>
    );
};

export default CustomPopover;
